import { isBrowser, prefersReducedMotion } from './base';

/** light = hiru (昼), dark = yozakura (夜桜), auto = suit le système. */
export type ThemeMode = 'light' | 'dark' | 'auto';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'hn-theme';
export const THEME_EVENT = 'hn-theme-change';

export interface ThemeChangeDetail {
  theme: ThemeMode;
  resolved: ResolvedTheme;
}

const root = () => document.documentElement;
const darkQuery = () => (isBrowser && typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null);

function readStorage(): ThemeMode | null {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return value === 'light' || value === 'dark' || value === 'auto' ? value : null;
  } catch {
    return null;
  }
}

function writeStorage(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    /* stockage indisponible (navigation privée…) : le thème reste appliqué pour la session */
  }
}

/** Current mode as set on <html data-hn-theme>. Defaults to "light". */
export function getTheme(): ThemeMode {
  if (!isBrowser) return 'light';
  const value = root().getAttribute('data-hn-theme');
  return value === 'dark' || value === 'auto' ? value : 'light';
}

/** Theme actually displayed ("auto" resolved through prefers-color-scheme). */
export function getResolvedTheme(mode: ThemeMode = getTheme()): ResolvedTheme {
  if (mode === 'auto') return darkQuery()?.matches ? 'dark' : 'light';
  return mode;
}

function notify(): void {
  const detail: ThemeChangeDetail = { theme: getTheme(), resolved: getResolvedTheme() };
  document.dispatchEvent(new CustomEvent<ThemeChangeDetail>(THEME_EVENT, { detail }));
}

export interface SetThemeOptions {
  /** Remember the choice in localStorage (default: true). */
  persist?: boolean;
  /**
   * Animate the change with the View Transitions API: the new theme grows as a
   * circle from this point (e.g. the toggle button). Ignored if unsupported or
   * if the user prefers reduced motion.
   */
  origin?: { x: number; y: number };
}

/** Applies a theme to the whole page. */
export function setTheme(mode: ThemeMode, options: SetThemeOptions = {}): void {
  if (!isBrowser) return;
  const { persist = true, origin } = options;
  const apply = () => {
    root().setAttribute('data-hn-theme', mode);
    if (persist) writeStorage(mode);
    notify();
  };

  const doc = document as Document & { startViewTransition?: (cb: () => void) => { ready: Promise<void> } };
  const willChange = getResolvedTheme(mode) !== getResolvedTheme();
  if (!origin || !willChange || !doc.startViewTransition || prefersReducedMotion()) {
    apply();
    return;
  }
  const transition = doc.startViewTransition(apply);
  const radius = Math.hypot(Math.max(origin.x, innerWidth - origin.x), Math.max(origin.y, innerHeight - origin.y));
  transition.ready
    .then(() => {
      root().animate(
        { clipPath: [`circle(0px at ${origin.x}px ${origin.y}px)`, `circle(${radius}px at ${origin.x}px ${origin.y}px)`] },
        { duration: 650, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', pseudoElement: '::view-transition-new(root)' },
      );
    })
    .catch(() => undefined);
}

/** Cycles light → dark (→ auto if `withAuto`). Returns the new mode. */
export function toggleTheme(options: SetThemeOptions & { withAuto?: boolean } = {}): ThemeMode {
  const order: ThemeMode[] = options.withAuto ? ['light', 'dark', 'auto'] : ['light', 'dark'];
  const current = options.withAuto ? getTheme() : getResolvedTheme();
  const next = order[(order.indexOf(current) + 1) % order.length];
  setTheme(next, options);
  return next;
}

/**
 * Restores the saved theme (or `fallback`) — call it as early as possible.
 * To avoid any flash before your bundle loads, inline `themeScript` in <head>.
 */
export function initTheme(fallback: ThemeMode = 'light'): ThemeMode {
  if (!isBrowser) return fallback;
  const mode = readStorage() ?? (root().hasAttribute('data-hn-theme') ? getTheme() : fallback);
  root().setAttribute('data-hn-theme', mode);
  return mode;
}

/** Calls `callback` whenever the theme changes, including OS changes in "auto". */
export function onThemeChange(callback: (detail: ThemeChangeDetail) => void): () => void {
  if (!isBrowser) return () => undefined;
  const onEvent = (event: Event) => callback((event as CustomEvent<ThemeChangeDetail>).detail);
  const query = darkQuery();
  const onSystem = () => {
    if (getTheme() === 'auto') callback({ theme: 'auto', resolved: getResolvedTheme() });
  };
  document.addEventListener(THEME_EVENT, onEvent);
  query?.addEventListener('change', onSystem);
  return () => {
    document.removeEventListener(THEME_EVENT, onEvent);
    query?.removeEventListener('change', onSystem);
  };
}

/** Inline snippet for <head> that applies the saved theme before first paint. */
export const themeScript = `(function(){try{var t=localStorage.getItem('${THEME_STORAGE_KEY}');if(t==='light'||t==='dark'||t==='auto')document.documentElement.setAttribute('data-hn-theme',t)}catch(e){}})();`;
